/**
 * Utility functions for handling API errors and extracting error messages
 */

export interface ApiError {
  data?: any;
  message?: string;
  statusMessage?: string;
  statusText?: string;
  status?: number;
  errors?: Record<string, any>;
}

/**
 * Extracts the most relevant error message from an API error response
 * @param error - The error object from API response
 * @param fallbackMessage - Default message if no error details found
 * @returns Formatted error message string
 */
export function extractApiErrorMessage(error: any, fallbackMessage: string = 'An error occurred. Please try again.'): string {
  if (!error) return fallbackMessage;
  
  // Check for different error response structures
  if (error.data && error.data.message) {
    return error.data.message;
  }
  
  if (error.message) {
    return error.message;
  }
  
  if (error.statusMessage) {
    return error.statusMessage;
  }
  
  if (error.statusText) {
    return error.statusText;
  }
  
  if (error.data && typeof error.data === 'string') {
    return error.data;
  }
  
  // Check for validation errors
  if (error.data && error.data.errors && typeof error.data.errors === 'object') {
    const validationErrors = Object.values(error.data.errors).flat();
    if (validationErrors.length > 0) {
      return validationErrors.join(', ');
    }
  }
  
  // Check for HTTP status codes
  if (error.status) {
    switch (error.status) {
      case 400:
        return 'Bad request. Please check your input.';
      case 401:
        return 'Unauthorized. Please log in again.';
      case 403:
        return 'Access denied. You don\'t have permission for this action.';
      case 404:
        return 'Resource not found.';
      case 422:
        return 'Validation error. Please check your input.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'Server error. Please try again later.';
      case 503:
        return 'Service unavailable. Please try again later.';
      default:
        return `Error ${error.status}. Please try again.`;
    }
  }
  
  return fallbackMessage;
}

/**
 * Formats validation errors into a readable string
 * @param errors - Validation errors object
 * @returns Formatted error string
 */
export function formatValidationErrors(errors: Record<string, any>): string {
  if (!errors || typeof errors !== 'object') return '';
  
  const errorMessages: string[] = [];
  
  Object.entries(errors).forEach(([field, messages]) => {
    if (Array.isArray(messages)) {
      messages.forEach(message => {
        if (message) {
          errorMessages.push(`${field}: ${message}`);
        }
      });
    } else if (messages) {
      errorMessages.push(`${field}: ${messages}`);
    }
  });
  
  return errorMessages.join(', ');
}

/**
 * Logs error details for debugging
 * @param error - The error object
 * @param context - Additional context information
 */
export function logApiError(error: any, context: string = 'API Error'): void {
  console.group(`🚨 ${context}`);
  console.error('Error object:', error);
  
  if (error?.data) {
    console.error('Error data:', error.data);
  }
  
  if (error?.status) {
    console.error('HTTP Status:', error.status);
  }
  
  if (error?.message) {
    console.error('Error message:', error.message);
  }
  
  console.groupEnd();
}
